import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
    { title: 'ENG', code: 'en' },
    { title: 'ՀԱՅ', code: 'am' },
    { title: 'РУС', code: 'ru' },
];

/** Shared language control: current language opens a dropdown (all breakpoints). */
type LanguageSwitcherProps = {
    withGlobe?: boolean;
    className?: string;
};

const LanguageSwitcher = ({ withGlobe = false, className = '' }: LanguageSwitcherProps) => {
    const { i18n } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectLanguage = (lang = 'am') => {
        i18n.changeLanguage(lang);
        localStorage.setItem('i18nextLng', lang);
        setIsMenuOpen(false);
    };

    useEffect(() => {
        if (!isMenuOpen) return;
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isMenuOpen]);

    const currentLanguage =
        languages.find((lang) => lang.code === i18n.language)?.title || 'ENG';

    return (
        <nav className={`ms-auto relative ${className}`} ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-haspopup="true"
                aria-expanded={isMenuOpen}
                className="flex items-center gap-2 font-bold text-xs px-2 py-2 rounded-md text-custom-gray-800"
            >
                {withGlobe ? (
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden
                    >
                        <path
                            d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                        />
                        <path
                            d="M2 12H22"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                        />
                        <path
                            d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                        />
                    </svg>
                ) : null}
                {currentLanguage}
            </button>

            {isMenuOpen ? (
                <ul
                    className="absolute right-0 mt-2 bg-white shadow-md border rounded-md z-20 min-w-[8rem]"
                    role="menu"
                >
                    {languages.map((lang) => (
                        <li
                            key={lang.code}
                            onClick={() => selectLanguage(lang.code)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    selectLanguage(lang.code);
                                }
                            }}
                            tabIndex={0}
                            className="cursor-pointer px-4 py-2 hover:bg-gray-100 text-xs font-bold"
                            role="menuitem"
                        >
                            {lang.title}
                        </li>
                    ))}
                </ul>
            ) : null}
        </nav>
    );
};

export default LanguageSwitcher;
